"use client";

import {
  Box,
  Field as ChakraField,
  Input,
  InputProps,
  Text,
  useControllableState,
} from "@chakra-ui/react";
import { useState, ReactNode } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { BaseFieldProps } from "./_types";
import { Field, FieldProps } from "@/_components/lib/ui/field";
import { PasswordStrength } from "./PasswordStrength";
import { FormErrorMessage } from "./_formErrMessage";
import { get } from "lodash";
import {
  sharedFieldStyles,
  sharedLabelFloatingStyles,
  formFieldFloatingWrapperStyles,
  formFieldOutlinedInputWrapperStyles,
} from "./_sharedFieldStyles";
import type { NormalizeFn } from "@/_utils/fieldNormalize";

interface FloatingLabelInputProps extends Omit<InputProps, "defaultValue"> {
  label: ReactNode;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  invalid?: boolean;
  helperText?: ReactNode;
  errorText?: ReactNode;
  isRequired?: boolean;
  floatingLabelProps?: React.ComponentProps<typeof ChakraField.Label>;
  normalize?: NormalizeFn;
}

export const FloatingLabelInput = (props: FloatingLabelInputProps) => {
  const {
    label,
    onValueChange,
    value,
    defaultValue = "",
    invalid,
    helperText,
    errorText,
    isRequired,
    floatingLabelProps,
    normalize,
    onFocus,
    onBlur,
    ...rest
  } = props;

  const [inputState, setInputState] = useControllableState({
    defaultValue,
    onChange: onValueChange,
    value,
  });

  const [focused, setFocused] = useState(false);
  const hasValue =
    inputState != null &&
    inputState !== "" &&
    String(inputState).trim().length > 0;
  const shouldFloat = hasValue || focused;
  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const normalized = normalize ? normalize(raw) : raw;
    setInputState(normalized);
    onValueChange?.(normalized);
  };

  return (
    <ChakraField.Root
      invalid={invalid}
      gap={1}
      pt={2.5}
      px={0}
      {...formFieldFloatingWrapperStyles}
    >
      <Box pos="relative" w="full">
        <Input
          {...rest}
          {...sharedFieldStyles}
          onFocus={(e) => {
            onFocus?.(e);
            setFocused(true);
          }}
          onBlur={(e) => {
            const raw = e.target.value;
            if (normalize) {
              const normalized = normalize(raw);
              if (normalized !== raw) {
                setInputState(normalized);
                onValueChange?.(normalized);
              }
            }
            onBlur?.(e);
            setFocused(false);
          }}
          onChange={onChangeHandler}
          value={inputState ?? ""}
          data-float={shouldFloat || undefined}
          h={10}
        />
        <ChakraField.Label
          id="label-floating"
          css={sharedLabelFloatingStyles}
          data-float={shouldFloat || undefined}
          {...floatingLabelProps}
        >
          {label}
          {isRequired && (
            <Text as="span" color="themeColor.form.required" mr={0.5}>
              *
            </Text>
          )}
        </ChakraField.Label>
      </Box>
      {helperText && !invalid && (
        <ChakraField.HelperText textStyle="xs" ml={3} display="none">
          {helperText}
        </ChakraField.HelperText>
      )}
      {errorText && (
        <ChakraField.ErrorText textStyle="xs" ml={3} mb={2}>
          {errorText}
        </ChakraField.ErrorText>
      )}
    </ChakraField.Root>
  );
};

interface InputFieldProps extends BaseFieldProps {
  inputStyles?: InputProps & { "data-testid"?: string };
  fieldStyles?: FieldProps;
  labelBuilder?: () => ReactNode;
  isRequired?: boolean;
  helperText?: string;
  isShowPasswordStrength?: boolean;
  bgColor?: string;
  fontFamily?: string;
  isFloating?: boolean;
  floatingLabelProps?: React.ComponentProps<typeof ChakraField.Label>;
  numberStep?: number;
  normalize?: NormalizeFn;
}

export const InputField = (props: InputFieldProps) => {
  const {
    name,
    placeholder,
    type = "text",
    label,
    inputStyles = {},
    labelBuilder,
    isRequired,
    helperText,
    fieldStyles = {},
    isShowPasswordStrength,
    isDisabled,
    bgColor = "themeColor.form.bgSolid",
    fontFamily = "body",
    isFloating = false,
    numberStep = 1,
    floatingLabelProps,
    normalize,
  } = props;

  const { control, formState, register, watch } = useFormContext();
  const labelComponent = labelBuilder ? labelBuilder() : label;

  if (isFloating) {
    const error = get(formState.errors, name);
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { defaultValue, ...restInputStyles } = inputStyles;
          const safeInputStyles: Omit<InputProps, "defaultValue"> =
            restInputStyles;
          return (
            <FloatingLabelInput
              label={labelComponent}
              placeholder={placeholder}
              type={type}
              value={field.value || ""}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              disabled={isDisabled}
              bgColor={bgColor}
              fontFamily={fontFamily}
              invalid={!!error}
              isRequired={isRequired}
              helperText={helperText}
              errorText={
                <FormErrorMessage errors={formState.errors} name={name} />
              }
              floatingLabelProps={floatingLabelProps}
              normalize={normalize}
              step={numberStep}
              {...safeInputStyles}
            />
          );
        }}
      />
    );
  }

  const error = get(formState.errors, name);
  const value = isShowPasswordStrength ? watch(name) : undefined;

  return (
    <Field
      {...fieldStyles}
      label={labelComponent}
      invalid={!!error}
      isRequired={isRequired}
      helperText={helperText}
      errorText={<FormErrorMessage errors={formState.errors} name={name} />}
      bg={bgColor}
      {...formFieldOutlinedInputWrapperStyles}
    >
      <Input
        {...register(name, {
          setValueAs: props.normalize,
        })}
        type={type}
        placeholder={placeholder}
        disabled={isDisabled}
        fontFamily={fontFamily}
        bgColor={bgColor}
        _placeholder={{ color: "themeColor.form.placeholder" }}
        {...sharedFieldStyles}
        {...inputStyles}
        fontSize={{ base: "md", md: "sm" }}
      />
      {isShowPasswordStrength && value && <PasswordStrength value={value} />}
    </Field>
  );
};
